import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PropertyList } from "../../utils/property-list";
import { PropertyListItem } from "../../utils/property-list-item";
import toast from "react-hot-toast";
import {
  accountCodeOptions,
  productStatus,
  productTypeOptions,
  unitOptions,
  warehouseStatusOptions,
} from "./product-data";
import PMListbox from "../../utils/PMListbox";
import axios from "../../../api/axios";
import { useSelector, useDispatch } from "react-redux";
import { getProductBrands } from "../../../slices/products/brands";
import {
  ArrowsRightLeftIcon,
  FolderIcon,
  SquaresPlusIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";
import ProductCategorySelector from "./Categories/selector/productCategorySelector";
import ModernProductSelectorV2 from "../../utils/Selector/ModernProductSelectorV2";
import ProductSelectorDialog from "../../utils/ProductSelectorDialog";

export const ProductSettings = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [productState, setProductState] = useState(props?.product || {});
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [accessoriesSelectorOpen, setAccessoriesSelectorOpen] = useState(false);
  const [sparePartsSelectorOpen, setSparePartsSelectorOpen] = useState(false);
  const [replacedBySelectorOpen, setReplacedBySelectorOpen] = useState(false);
  const [categoriesSelectorOpen, setCategoriesSelectorOpen] = useState(false);
  const [changed, setChanged] = useState(false);
  const product = productState;

  const handleOpenSelector = (selector) => {
    if (selector === "accessories") {
      setAccessoriesSelectorOpen(true);
      setSelectorOpen(true);
    } else if (selector === "spare_parts") {
      setSparePartsSelectorOpen(true);
      setSelectorOpen(true);
    } else if (selector === "replaced_by") {
      setReplacedBySelectorOpen(true);
      setSelectorOpen(true);
    } else if (selector === "categories") {
      setCategoriesSelectorOpen(true);
      setSelectorOpen(true);
    } else {
      toast.error("Could not open selector. Missing selector attribute");
      return false;
    }
  };

  function handleOpenSelectorCallback() {
    setSelectorOpen(false);
    setAccessoriesSelectorOpen(false);
    setSparePartsSelectorOpen(false);
    setReplacedBySelectorOpen(false);
    setCategoriesSelectorOpen(false);
  }

  const updateSettings = async () => {
    // Data to be sent to query
    const request = {
      product: {
        status: productState.status,
        warehouse_status_type_id: productState.warehouse_status_type_id,
        account_coding_id: productState.account_coding_id,
        product_type: productState.product_type,
        unit_id: productState.unit_id,
        brand_id: productState.brand,
        spare_parts: productState.spare_parts,
        accessories: productState.accessories,
        replaced_by_product_id: productState.replaced_by_product_id ?? null,
      },
    };
    
    try {
      // Axios PUT request
      const response = await axios.put(`/products/${productState.id}`, request);
      
      if (response.status === 500) throw new Error("Server error");
      if (response.data?.id) {
        if (props?.onRefresh) props?.onRefresh(response?.data);
        setProductState(response?.data);
        setChanged(false);
        toast.success("Data was saved.");
      } else {
        toast.error("Something went wrong...could not save!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong...could not save!");
    }
  };



  const handleListboxChange = (e) => {
    setChanged(true);
    setProductState((prevState) => ({ ...prevState, [e.name]: e.value }));
  };

  useEffect(() => {
    if (changed) {
      updateSettings();
    }
  }, [changed]); // Only depend on 'changed' flag, not 'productState'

  useEffect(() => {
    dispatch(getProductBrands());
  }, []);

  useEffect(() => {
    setProductState(props?.product || {});
  }, [props?.product]);

  const brandState = useSelector((store) => {
    return store.product_brands;
  });

  const productBrands = () => {
    const brands = [{ name: "brand", label: "Ikke valgt", value: 0 }];
    brandState?.data?.map((brand) => {
      brands.push({
        name: "brand",
        label: brand.title,
        value: brand.id,
      });
    });
    return brands;
  };

  return (
    <>
      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("Product Settings")}
            </h3>
            <p className="mt-1 text-sm text-gray-500"></p>
          </div>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                label={t("Status")}
                options={productStatus}
                selected={productState?.status}
                onChange={handleListboxChange}
              />
            </div>

            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                label={t("product.brand")}
                options={productBrands()}
                selected={productState?.brand?.id || productState?.brand}
                onChange={handleListboxChange}
              />
            </div>

            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                label={t("Product Type")}
                options={productTypeOptions}
                selected={productState.product_type}
                onChange={handleListboxChange}
              />
            </div>

            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                label={t("Unit")}
                options={unitOptions}
                selected={productState?.unit_id}
                onChange={handleListboxChange}
              />
            </div>

            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                label={t("Account Code")}
                options={accountCodeOptions}
                selected={productState.account_coding_id}
                onChange={handleListboxChange}
              />
            </div>

            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                label={t("Warehouse Status Type")}
                options={warehouseStatusOptions}
                selected={productState.warehouse_status_type_id}
                onChange={handleListboxChange}
              />
            </div>

            <div className="col-span-6 sm:col-span-6">
              <PropertyList cols={1}>
                <PropertyListItem
                  label={t("Spare parts")}
                  value={product?.spare_parts?.length + " selected"}
                  icon={WrenchIcon}
                  onClick={() => handleOpenSelector("spare_parts")}
                />
                <PropertyListItem
                  label={t("Categories")}
                  value={product?.categories?.length + " selected"}
                  icon={FolderIcon}
                  onClick={() => handleOpenSelector("categories")}
                />
                <PropertyListItem
                  label={t("Accessories")}
                  value={product?.accessories?.length + " selected"}
                  icon={SquaresPlusIcon}
                  onClick={() => handleOpenSelector("accessories")}
                />
                <PropertyListItem
                  label={t("product.replaced_by_product")}
                  value={
                    product?.replaced_by_product
                      ? [
                          product.replaced_by_product.item_number,
                          product.replaced_by_product.title,
                        ]
                          .filter(Boolean)
                          .join(" — ") || t("None")
                      : t("None")
                  }
                  icon={ArrowsRightLeftIcon}
                  onClick={() => handleOpenSelector("replaced_by")}
                />
              </PropertyList>
            </div>

            <div className="col-span-6"></div>
          </div>
        </div>
      </div>
      <ProductSelectorDialog
        open={accessoriesSelectorOpen}
        onClose={() => handleOpenSelectorCallback()}
        title={t("Select Accessories")}
        size="7xl"
        closeOnOutsideClick={true}
        zIndex={3000}
      >
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <ModernProductSelectorV2
            multiple={true}
            open={accessoriesSelectorOpen}
            embeddedInParentDialog={true}
            onClose={() => handleOpenSelectorCallback()}
            selectedProducts={(product.accessories || [])
              .map(id => {
                const productId = typeof id === 'object' ? id.id : id;
                const numId = parseInt(productId);
                return isNaN(numId) ? null : { id: numId };
              })
              .filter(item => item !== null)}
            filters={product.id ? [{ name: "exclude_ids", value: JSON.stringify([product.id]) }] : []}
            showHeader={false}
            showQuantityInput={false}
            onChange={(data) => {
              // Update the product state with selected accessories
              setChanged(true);
              setProductState(prev => {
                const newState = {
                  ...prev,
                  accessories: data.map(item => item.id)
                };
                return newState;
              });
            }}
          />
        </div>
      </ProductSelectorDialog>

      <ProductSelectorDialog
        open={sparePartsSelectorOpen}
        onClose={() => handleOpenSelectorCallback()}
        title={t("Select Spare Parts")}
        size="7xl"
        closeOnOutsideClick={true}
        zIndex={3000}
      >
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <ModernProductSelectorV2
            multiple={true}
            open={sparePartsSelectorOpen}
            embeddedInParentDialog={true}
            onClose={() => handleOpenSelectorCallback()}
            selectedProducts={(product.spare_parts || [])
              .map(id => {
                const productId = typeof id === 'object' ? id.id : id;
                const numId = parseInt(productId);
                return isNaN(numId) ? null : { id: numId };
              })
              .filter(item => item !== null)}
            filters={product.id ? [{ name: "exclude_ids", value: JSON.stringify([product.id]) }] : []}
            showHeader={false}
            showQuantityInput={false}
            onChange={(data) => {
              // Update the product state with selected spare parts
              setChanged(true);
              setProductState(prev => {
                const newState = {
                  ...prev,
                  spare_parts: data.map(item => item.id)
                };
                return newState;
              });
            }}
          />
        </div>
      </ProductSelectorDialog>

      <ProductSelectorDialog
        open={replacedBySelectorOpen}
        onClose={() => handleOpenSelectorCallback()}
        title={t("product.replaced_by_product")}
        size="7xl"
        closeOnOutsideClick={true}
        zIndex={3000}
      >
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <ModernProductSelectorV2
            multiple={false}
            open={replacedBySelectorOpen}
            embeddedInParentDialog={true}
            onClose={() => handleOpenSelectorCallback()}
            selectedProducts={
              product.replaced_by_product?.id
                ? [{ id: parseInt(product.replaced_by_product.id, 10) }]
                : product.replaced_by_product_id
                ? [{ id: parseInt(product.replaced_by_product_id, 10) }]
                : []
            }
            filters={
              product.id
                ? [{ name: "exclude_ids", value: JSON.stringify([product.id]) }]
                : []
            }
            showHeader={false}
            showQuantityInput={false}
            onChange={(data) => {
              setChanged(true);
              setProductState((prev) => {
                const picked = data?.[0];
                return {
                  ...prev,
                  replaced_by_product_id: picked?.id ?? null,
                  replaced_by_product: picked
                    ? {
                        id: picked.id,
                        item_number: picked.item_number,
                        title: picked.title,
                        displayTitle: picked.displayTitle,
                      }
                    : null,
                };
              });
            }}
          />
        </div>
      </ProductSelectorDialog>

      {/* <ItemSelector
        {...props}
        open={categoriesSelectorOpen}
        selectorCloseCallback={handleOpenSelectorCallback}
        selectorType="categories"
        module={"product"}
        module_param={product.id}
        selectedItems={product.category_ids}
      /> */}

      <ProductCategorySelector
        open={categoriesSelectorOpen}
        selectorCloseCallback={handleOpenSelectorCallback}
        selectedItems={product.category_ids}
        module={"product"}
        module_param={product.id}
      />
    </>
  );
};
